import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { get, set } from "lodash";
import { FC, FormEvent, useContext, useState } from "react";
import { HistoryProps } from "../..";
import { medicalsApi } from "../../../../../../api/medicalsApi";
import ComponentLoader from "../../../../../../components/ComponentLoader";
import {
  NotificationVariant,
  NotificationsContext,
} from "../../../../../../components/dashboard/NotificationsProvider";
import NotesInput from "../../../../../../components/form/NotesInput";
import TypeAheadInput from "../../../../../../components/form/TypeAheadInput/TypeAheadInput";
import Quantity from "../../../../../../components/widgets/inputs/Quantity";
import CodingSystems from "../../../../../../constants/CodingSystems";
import useMedicalEncounter from "../../../../../../hooks/useMedicalEncounter";
import { severityCodes } from "../../../../../../slices/options/allergy";
import {
  conditionClinicalStatuses,
  conditionVerificationStatuses,
} from "../../../../../../slices/options/condition";
import {
  createCodeableConcept,
  isCodeableConceptEqualToValue,
} from "../../../../../../utils/DataUtils";
import DialogPrompt from "../../../components/Dialog";
import { useDictationCheck } from "../../../../../../hooks/useDictationCheck";

const MedicalHistoryForm: FC<HistoryProps> = ({
  item,
  patientReference,
  disabled,
  removeHistoryItem,
  setSaved,
  updateHistoryItem,
}) => {
  const [loading, toggleLoader] = useState(false);
  const [intItem, setIntIem] = useState(item || {});
  const { setNotification } = useContext(NotificationsContext);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const {
    encounterReference,
    setEncounterToInProgress,
    refreshPatientHistory,
  } = useMedicalEncounter();
  const { isDictationActive } = useDictationCheck();

  const updateCondition = (field, value) => {
    const condition = { ...intItem };
    set(condition, field, value);
    setIntIem(condition);
  };

  const saveMedicalCondition = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      toggleLoader(true);
      const medicalCondition = {
        ...intItem,
        category: createCodeableConcept(
          CodingSystems.CONDITION_CATEGORY,
          "problem-list-item",
          "	Problem List Item",
        ),
      };

      if (patientReference) {
        medicalCondition.subject = patientReference;
      }

      if (encounterReference) {
        medicalCondition.encounter = encounterReference;
      }

      let update;

      if (medicalCondition.id) {
        update = await medicalsApi.updateCondition(
          medicalCondition.id,
          {
            ...medicalCondition,
          },
          {
            encounter: encounterReference?.reference,
          },
        );
        setIntIem(update);
      } else {
        update = await medicalsApi.saveCondition(
          {
            ...medicalCondition,
          },
          {
            encounter: encounterReference?.reference,
          },
        );
        setIntIem(update);
      }

      // can be changed to await
      setEncounterToInProgress();

      updateHistoryItem(update);
      refreshPatientHistory();

      const clinicalStatus = get(update, "clinicalStatus.coding[0].code");

      const verificationStatus = get(
        update,
        "verificationStatus.coding[0].code",
      );

      if ([verificationStatus, clinicalStatus].includes("entered-in-error")) {
        try {
          removeHistoryItem();
        } catch (error) {
          console.error(error);
        }
      }

      setNotification({
        message: "Successfully saved the patient's medical history.",
        variant: NotificationVariant.success,
      });

      setSaved(true);
    } catch (e) {
      setNotification({
        message: "Unable to save the patient's medical history.",
        variant: NotificationVariant.error,
      });
    } finally {
      toggleLoader(false);
    }
  };

  const onDelete = () => {
    if (intItem.id) {
      setOpenDialog(true);
    } else {
      try {
        removeHistoryItem();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const deleteMedicalHistory = async () => {
    try {
      toggleLoader(true);
      const medicalCondition = { ...intItem };
      const response = await medicalsApi.updateCondition(
        medicalCondition.id,
        {
          ...medicalCondition,
          clinicalStatus: "entered-in-error",
          verificationStatus: createCodeableConcept(
            CodingSystems.CONDITION_VERIFICATION_STATUS,
            "entered-in-error",
            "Entered in Error",
          ),
        },
        {
          encounter: encounterReference?.reference,
        },
      );
      setIntIem(response);
      updateHistoryItem(response);
      refreshPatientHistory();
      setOpenDialog(false);
      removeHistoryItem();
    } catch (e) {
      console.error(
        `An error occured while deleting the patient's medical history. Please try again.${e}`,
      );
      setNotification({
        variant: NotificationVariant.error,
        message: e.message,
      });
    } finally {
      toggleLoader(false);
    }
  };

  const isValid = () => !!intItem?.code?.text;

  return (
    <>
      <form onSubmit={saveMedicalCondition}>
        <DialogPrompt
          openPrompt={openDialog}
          content="You are about to delete a medical item for this patient and this action is permanent. Are you sure?"
          title="Delete Medical History"
          button1="Cancel"
          button2="Delete"
          button1Click={() => setOpenDialog(false)}
          button2Click={deleteMedicalHistory}
        />
        <Grid container spacing={3} xs={12}>
          <Grid item xs={12} style={{ paddingTop: 10 }}>
            <TypeAheadInput
              required
              freeOptions
              optionType="snomed"
              subset={["disorder", "finding", "event", "situation"]}
              value={get(intItem, "code")}
              variant="outlined"
              disabled={disabled}
              onChange={(val) => {
                if (val) {
                  const code = createCodeableConcept(
                    CodingSystems.ENCOUNTER_REASON,
                    val.snomedCID,
                    val.snomedFSN,
                  );

                  updateCondition("code", code);
                  val = undefined;
                } else {
                  updateCondition("code", null);
                }

                setSaved(false);
              }}
              getValueLabel={(v) => get(v, "text")}
              fieldLabel="Medical Condition"
              getOptionLabel={(o) => o.snomedFSN}
              isOptionEqualToValue={(option, value) =>
                isCodeableConceptEqualToValue(option, value)
              }
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl variant="outlined" fullWidth>
              <TextField
                select
                value={get(intItem, "severity.coding[0].code")}
                disabled={disabled}
                onChange={(e) => {
                  const code = e.target.value;
                  const opt = severityCodes.find((o) => o.code === code);
                  if (opt) {
                    const codeableConcept = createCodeableConcept(
                      CodingSystems.CONDITION_SEVERITY,
                      opt.code,
                      opt.display,
                    );
                    updateCondition("severity", codeableConcept);
                  } else {
                    updateCondition("severity", null);
                  }
                  setSaved(false);
                }}
                label="Severity"
                InputLabelProps={{ shrink: true }}
              >
                {severityCodes.map((c, index) => (
                  <MenuItem value={c.code} key={index}>
                    {c.display}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl variant="outlined" fullWidth>
              <TextField
                select
                value={get(intItem, "clinicalStatus.coding[0].code")}
                disabled={disabled}
                defaultValue="active"
                onChange={(e) => {
                  const code = e.target.value;
                  const opt = conditionClinicalStatuses.find(
                    (o) => o.code === code,
                  );

                  if (opt) {
                    const codeableConcept = createCodeableConcept(
                      CodingSystems.CONDITION_CLINICAL_STATUS,
                      opt.code,
                      opt.display,
                    );
                    updateCondition("clinicalStatus", codeableConcept);
                  } else {
                    updateCondition("clinicalStatus", null);
                  }
                  setSaved(false);
                }}
                label="Clinical Status"
                InputLabelProps={{ shrink: true }}
              >
                {conditionClinicalStatuses.map((c, index) => (
                  <MenuItem value={c.code} key={index}>
                    {c.display}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl variant="outlined" fullWidth>
              <TextField
                select
                value={get(intItem, "verificationStatus.coding[0].code")}
                disabled={disabled}
                defaultValue="confirmed"
                onChange={(e) => {
                  const code = e.target.value;
                  const opt = conditionVerificationStatuses.find(
                    (o) => o.code === code,
                  );
                  if (opt) {
                    const codeableConcept = createCodeableConcept(
                      CodingSystems.CONDITION_VERIFICATION_STATUS,
                      opt.code,
                      opt.display,
                    );

                    updateCondition("verificationStatus", codeableConcept);
                  } else {
                    updateCondition("verificationStatus", null);
                  }
                  setSaved(false);
                }}
                label="Verification Status"
                InputLabelProps={{ shrink: true }}
              >
                {conditionVerificationStatuses.map((c, index) => (
                  <MenuItem value={c.code} key={index}>
                    {c.display}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Quantity
              fieldLabel="Age of Diagnosis"
              disabled={disabled}
              quantity={get(intItem, "onset") || {}}
              setQuantity={(val) => {
                updateCondition("onset", val);
                setSaved(false);
              }}
              defaultUnitCode="a"
              optionKey="timeSpans"
              codingSystem={CodingSystems.UNITS_OF_MEASURE}
              size={undefined}
              min={0}
            />
          </Grid>
          <Grid item xs={12}>
            <NotesInput
              disabled={disabled}
              value={intItem?.note || []}
              label="Comments/Notes"
              onChange={(note) => setIntIem({ ...intItem, note })}
              placeholderText="Enter comments/notes about the patient's gynaecological history"
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" sx={{ gap: 1 }}>
              <Button
                color="secondary"
                variant="outlined"
                size="small"
                disabled={disabled}
                onClick={onDelete}
              >
                Delete
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={disabled || !isValid() || isDictationActive}
                size="small"
              >
                Save
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
      <ComponentLoader loading={loading} />
    </>
  );
};

export default MedicalHistoryForm;
